import { IAddress } from 'services/GoogleMapsService';
import { isEmpty } from 'utils/generalUtils';
import { isNYState } from 'utils/addressUtils';

export const parseAddressComponents = (place: google.maps.places.PlaceResult): IAddress | null => {
  if (!place.address_components) return null;

  const componentMap: Record<string, string> = {};

  place.address_components.forEach(component => {
    // Store by all types, not just the first one
    component.types.forEach(type => {
      if (!componentMap[type]) {
        componentMap[type] = component.long_name;
        if (type === 'administrative_area_level_1' || type === 'country') {
          componentMap[`${type}_short`] = component.short_name;
        }
      }
    });
  });

  const addressLine1Parts = [componentMap['street_number'], componentMap['route']].filter(Boolean);
  const addressLine1 = addressLine1Parts.join(' ');

  const addressLine2Parts = [
    componentMap['subpremise'], // Apt, Suite
    componentMap['floor'],
    componentMap['room'],
    componentMap['premise'],
  ].filter(Boolean);
  const addressLine2 = addressLine2Parts.join(', ');

  // Extract latitude and longitude from geometry if available
  const latitude = place.geometry?.location?.lat();
  const longitude = place.geometry?.location?.lng();

  // City logic: If no locality and state is NY, default to "New York" as city
  const locality = componentMap['locality'] || componentMap['sublocality_level_1'];
  const city =
    isEmpty(locality) && isNYState(componentMap['administrative_area_level_1_short']) ? 'New York' : locality;

  return {
    streetNumber: componentMap['street_number'],
    streetName: componentMap['route'],
    addressLine1,
    addressLine2,
    city,
    county: componentMap['administrative_area_level_2'],
    borough: componentMap['sublocality_level_1'] || componentMap['sublocality'], // NYC Borough
    state: componentMap['administrative_area_level_1'],
    stateCode: componentMap['administrative_area_level_1_short'],
    country: componentMap['country'],
    countryCode: componentMap['country_short'],
    postalCode: componentMap['postal_code'],
    latitude,
    longitude,
    formattedAddress: place?.formatted_address || '',
  };
};

export const createAutocompleteOptions = (allowedCountries: string[]): google.maps.places.AutocompleteOptions => ({
  fields: ['address_components', 'geometry', 'formatted_address'],
  types: ['address'],
  ...(allowedCountries.length > 0 && {
    componentRestrictions: {
      country: allowedCountries.map(c => c.toLowerCase()),
    },
  }),
});
